import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { OnboardingPath, PersonaAnswers } from "@/model/interface/Onboarding";

interface PersonaStepProps {
  path: OnboardingPath;
  value: PersonaAnswers;
  onChange: (next: PersonaAnswers) => void;
}

const INVESTOR_LEAD_SOURCES = [
  "PPC / paid ads",
  "Direct mail",
  "Cold calling",
  "SMS marketing",
  "Driving for dollars",
  "Wholesalers / dispositions network",
  "List pulls (PropStream, BatchLeads, etc.)",
  "SEO / inbound web",
  "Referrals",
];

export default function PersonaStep({ path, value, onChange }: PersonaStepProps) {
  const showInvestor = path === "INVESTOR" || path === "BOTH";
  const showAgent = path === "AGENT" || path === "BOTH";

  const update = (patch: Partial<PersonaAnswers>) => {
    onChange({ ...value, ...patch });
  };

  const toggleLeadSource = (source: string) => {
    const next = value.leadSources.includes(source)
      ? value.leadSources.filter((s) => s !== source)
      : [...value.leadSources, source];
    update({ leadSources: next });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">A bit more about your work</h2>
        <p className="text-sm text-muted-foreground">
          We use this to tailor your initial setup.
        </p>
      </div>

      {showInvestor && (
        <div className="space-y-3">
          <Label>Primary lead sources (select all that apply)</Label>
          <div className="grid grid-cols-2 gap-2">
            {INVESTOR_LEAD_SOURCES.map((source) => (
              <label
                key={source}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
              >
                <Checkbox
                  checked={value.leadSources.includes(source)}
                  onCheckedChange={() => toggleLeadSource(source)}
                />
                <span className="text-sm">{source}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {showAgent && (
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={value.mlsMembership}
              onCheckedChange={(checked: boolean) => update({ mlsMembership: checked === true })}
            />
            <span className="text-sm">I have an active MLS membership</span>
          </label>

          <div className="space-y-1.5">
            <Label htmlFor="licenseNumber">Real estate license number (optional)</Label>
            <Input
              id="licenseNumber"
              value={value.licenseNumber}
              onChange={(e) => update({ licenseNumber: e.target.value })}
              placeholder="e.g. SA12345678"
            />
          </div>
        </div>
      )}
    </div>
  );
}
